import type { AnchorHTMLAttributes } from 'react';

import HighlightedText from 'components/HighlightedText';
import Img from 'components/Img';
import { OptionalLink } from 'components/Link';
import Price from 'components/Price';
import Text from 'components/Text';
import { useTrackEvent } from 'hooks';
import type { ProductCard } from 'models/productCard';
import { getProductImage } from 'utils/business-logic';
import { cn } from 'utils/classNames';

interface Props extends AnchorHTMLAttributes<HTMLAnchorElement> {
	/** Additional class names on the anchor. */
	className?: string;

	/** Text to highlight in the product title. */
	highlight?: string;

	/** Product data. */
	product: ProductCard;
}

/** A slim line for a product with image, title and price. */
export default function ProductLine({
	className,
	highlight,
	onClick,
	product,
	...attrs
}: Props) {
	const { sendProductClickEvent } = useTrackEvent();
	const image = getProductImage(product);

	return (
		<OptionalLink
			key={product.id}
			href={product.url}
			className={cn('group/product-line flex items-center', className)}
			onLinkClick={(e) => {
				sendProductClickEvent(product.id);
				onClick?.(e);
			}}
			{...attrs}
		>
			<Img
				src={image?.src}
				alt={image?.alt}
				width={40}
				height={40}
				loading="eager"
				className="mr-2 size-10 shrink-0"
			/>
			<Text
				as="span"
				className={cn(
					'mr-6 grow',
					product.url && 'group-hover/product-line:underline',
				)}
			>
				{highlight ? (
					<HighlightedText text={product.title} highlight={highlight} />
				) : (
					product.title
				)}
			</Text>
			{!product.expired && product.displayPrice && (
				<Price price={product.displayPrice} size="xs" />
			)}
		</OptionalLink>
	);
}
ProductLine.displayName = 'ProductLine';
